import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { api } from "../lib/api";
import { UploadSimple, FileText, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * RAG file upload + listing.
 *
 * All uploads go to the `foundation` pool (every agent retrieves it).
 *
 * Two render modes:
 *   - default: large dashed-border dropzone + file list (used historically).
 *   - compact: hidden file input + compact pill list. Parent triggers the
 *     native picker via ref.openPicker(). Used by the simplified Triád
 *     where the dropzone is replaced by a small "Fájl" header button.
 */
const FileUpload = forwardRef(function FileUpload(
  { accentColor = "#5e50f7", onCountChange, hideTitle = false, flatBg = false, compact = false },
  ref,
) {
  const SCOPE = "foundation";
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openPicker: () => inputRef.current?.click(),
  }), []);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/rag/files?agent_type=${SCOPE}`);
      setFiles(res.data.files || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (typeof onCountChange === "function") onCountChange(files.length);
  }, [files.length, onCountChange]);

  const handleUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      for (const f of fileList) {
        const form = new FormData();
        form.append("file", f);
        form.append("agent_type", SCOPE);
        await api.post("/rag/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Feltöltve a Tudásbázisba");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feltöltés sikertelen");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/rag/files/${id}`);
      load();
    } catch {
      toast.error("Törlés sikertelen");
    }
  };

  // ─── Compact mode: hidden input + flat pill list (no dropzone). ───
  if (compact) {
    return (
      <div data-testid="rag-upload-compact">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.md"
          onChange={(e) => handleUpload(Array.from(e.target.files || []))}
          className="hidden"
        />
        {(uploading || files.length > 0) && (
          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}
            data-testid="rag-files-compact"
          >
            {uploading && (
              <span
                style={{
                  background: "rgba(124,108,242,0.06)",
                  border: "1px solid rgba(124,108,242,0.18)",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 12,
                  color: "#a89bff",
                }}
              >
                Feltöltés…
              </span>
            )}
            {files.map((f) => (
              <span
                key={f.id}
                className="inline-flex items-center"
                style={{
                  background: "rgba(124,108,242,0.06)",
                  border: "1px solid rgba(124,108,242,0.18)",
                  borderRadius: 8,
                  padding: "6px 10px 6px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  color: "#d4d4e0",
                }}
                data-testid={`rag-pill-${f.id}`}
              >
                <FileText size={12} color={accentColor} weight="bold" />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 220,
                  }}
                  title={f.original_filename}
                >
                  {f.original_filename}
                </span>
                <span style={{ fontSize: 11, color: "#6a6a80" }}>
                  {(f.chunk_count || 0)} chunk · {Math.round(f.size / 1024)} KB
                </span>
                <button
                  type="button"
                  onClick={() => handleDelete(f.id)}
                  style={{
                    color: "#a1a1a1",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: 14,
                    lineHeight: 1,
                  }}
                  aria-label="Fájl törlése"
                  data-testid={`rag-delete-${f.id}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── Default mode (legacy dropzone) ───
  const dropStyle = flatBg
    ? {
        background: dragging ? "rgba(94,80,247,0.06)" : "transparent",
        border: `1px dashed ${dragging ? accentColor : "rgba(124,108,242,0.25)"}`,
      }
    : {
        background: dragging
          ? "linear-gradient(180deg, rgba(94,80,247,0.14) 0%, rgba(94,80,247,0.06) 100%)"
          : "linear-gradient(180deg, rgba(94,80,247,0.06) 0%, rgba(94,80,247,0.02) 100%)",
        border: `1.5px dashed ${dragging ? accentColor : "rgba(94,80,247,0.32)"}`,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02)",
      };

  return (
    <div data-testid="rag-upload-foundation">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        {!hideTitle && <div className="mex-eyebrow">Tudásbázis</div>}
        {files.length > 0 && (
          <span className="text-[10px] text-[#7c7c8a] tracking-wider">
            {files.length} fájl indexelve
          </span>
        )}
      </div>

      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleUpload(Array.from(e.dataTransfer.files));
        }}
        className="block rounded-xl p-5 text-center cursor-pointer transition-all"
        style={dropStyle}
        data-testid="rag-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.md"
          onChange={(e) => handleUpload(Array.from(e.target.files || []))}
          className="hidden"
        />
        <div className="flex flex-col items-center gap-2">
          <UploadSimple size={22} color={accentColor} />
          <div className="text-[13px] text-white font-medium">
            {uploading ? "Feltöltés…" : "Húzd ide, vagy kattints a tallózáshoz"}
          </div>
          <div className="text-[11px] text-[#a8a8b8]">
            PDF, DOC, DOCX, TXT · max 15MB
          </div>
        </div>
      </label>

      {files.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="rag-files">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-[12px]"
              style={{
                background: "rgba(94,80,247,0.04)",
                border: "1px solid rgba(94,80,247,0.14)",
              }}
            >
              <FileText size={14} color={accentColor} />
              <span className="flex-1 truncate text-white">{f.original_filename}</span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{ background: "rgba(94,80,247,0.10)", color: "#c3c0e8" }}
                title={`${f.chunk_count || 0} chunk indexelve`}
              >
                {(f.chunk_count || 0)} chunk
              </span>
              <span className="text-[11px] text-[#a8a8b8] tabular-nums">
                {Math.round(f.size / 1024)} KB
              </span>
              <button
                onClick={() => handleDelete(f.id)}
                className="text-[#a1a1a1] hover:text-red-400 transition"
                aria-label="Törlés"
                data-testid={`rag-delete-${f.id}`}
              >
                <Trash size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default FileUpload;
